import { ReactNode } from 'react';

import RedoIcon from '@mui/icons-material/Redo';
import UndoIcon from '@mui/icons-material/Undo';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { visuallyHidden } from '@mui/utils';
import { GridCloseIcon } from '@mui/x-data-grid';
import { Link } from 'react-router';
import { CodeEditor } from 'shared/base';

export function ReportYamlEditorWrapper({
  canRedoChanges,
  canUndoChanges,
  children,
  currentlyViewedReportYaml,
  editorOpen,
  editorYaml,
  handleRedoChanges,
  handleSave,
  handleUndoChanges,
  handleViewPreview,
  isNew,
  isReportYamlModified,
  onCancel,
  onCreate,
  setEditorOpen,
  setEditorYaml,
  yamlParsingError,
}: {
  canRedoChanges: boolean;
  canUndoChanges: boolean;
  children: ReactNode;
  currentlyViewedReportYaml?: string;
  editorOpen: boolean;
  editorYaml?: string;
  handleRedoChanges: () => void;
  handleSave: () => void;
  handleUndoChanges: () => void;
  handleViewPreview: () => void;
  isNew?: boolean;
  isReportYamlModified: boolean;
  onCancel?: () => void;
  onCreate?: () => void;
  setEditorOpen: (editorOpen: boolean) => void;
  setEditorYaml: (editorYaml: string) => void;
  yamlParsingError?: string;
}) {
  const theme = useTheme();

  return (
    <>
      <Box
        sx={{
          backgroundColor: 'background.paper',
          bottom: 0,
          height: '100vh',
          overflow: 'hidden',
          padding: 1,
          paddingX: 3,
          position: 'absolute',
          right: 0,
          top: 0,
          transform: editorOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.16s ease-in-out',
          width: '48%', // -2% for a small gap
        }}
      >
        <Stack
          alignItems="flex-start"
          direction="row"
          justifyContent="space-between"
          paddingY={2}
          spacing={2}
        >
          <Box>
            <Typography variant="h6">Configure report</Typography>
            <Typography variant="caption">Edit the YAML to make changes to the report</Typography>
          </Box>
          {isNew ? (
            <Stack direction="row" spacing={1}>
              <Button onClick={onCreate} size="small" variant="contained">
                Save this report
              </Button>
              <Button color="secondary" onClick={onCancel} size="small" variant="outlined">
                Cancel
              </Button>
            </Stack>
          ) : (
            <IconButton onClick={() => setEditorOpen(false)}>
              <GridCloseIcon />
            </IconButton>
          )}
        </Stack>
        <Stack
          spacing={1}
          sx={{
            backgroundColor: theme.palette.secondary.darkMedium,
            height: '100%',
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            spacing={2}
            sx={{ borderBottom: '1px solid', borderColor: 'divider', p: 1 }}
          >
            <Stack direction="row" spacing={0.5}>
              {!isNew && (
                <Button
                  disabled={!isReportYamlModified}
                  onClick={() => handleSave()}
                  size="small"
                  variant="contained"
                >
                  Save changes
                </Button>
              )}
              <Button
                color="secondary"
                disabled={editorYaml === currentlyViewedReportYaml}
                onClick={handleViewPreview}
                size="small"
                variant="outlined"
              >
                Preview changes
              </Button>
              <Button
                color="secondary"
                disabled={!canUndoChanges}
                onClick={handleUndoChanges}
                size="small"
                sx={{ minWidth: 'auto' }}
                variant="outlined"
              >
                <UndoIcon sx={{ fontSize: 16 }} />
                <Typography sx={{ ...visuallyHidden }} variant="caption">
                  Undo changes
                </Typography>
              </Button>
              <Button
                color="secondary"
                disabled={!canRedoChanges}
                onClick={handleRedoChanges}
                size="small"
                sx={{ minWidth: 'auto' }}
                variant="outlined"
              >
                <RedoIcon sx={{ fontSize: 16 }} />
                <Typography sx={{ ...visuallyHidden }} variant="caption">
                  Redo changes
                </Typography>
              </Button>
            </Stack>
            <Button
              color="secondary"
              component={Link}
              size="small"
              target="_blank"
              to={`https://www.cloudquery.io/docs/platform/features/reports`}
              variant="outlined"
            >
              Documentation
            </Button>
          </Stack>
          <CodeEditor
            language="yaml"
            onChange={setEditorYaml}
            options={{
              minimap: { enabled: false },
            }}
            propSlots={{
              editor: {
                colors: {
                  'editor.background': theme.palette.secondary.darkMedium!,
                },
              },
            }}
            value={editorYaml ?? ''}
          />
        </Stack>
      </Box>
      <Box
        sx={{
          transform: editorOpen ? 'scale(0.5)' : 'scale(1)',
          transformOrigin: 'top left',
          transition:
            'transform 0.16s ease-in-out, width 0.16s ease-in-out, padding 0.16s ease-in-out',
        }}
      >
        {yamlParsingError ? (
          <Alert severity="error">
            <AlertTitle>Error parsing YAML</AlertTitle>
            {yamlParsingError}
          </Alert>
        ) : (
          children
        )}
      </Box>
    </>
  );
}
